import { Action } from "@packages/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	getCashFlowReportQueryOptions,
	getPnLReportQueryOptions,
	listFinancialTransactionsQueryOptions,
	listReceivablesQueryOptions,
} from "@/api/financial";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useFarmScope } from "@/hooks/useFarmScope";
import { formatMoney, formatMoneyCompact } from "@/utils/financial";
import { AttentionLane } from "./-components/AttentionLane";
import { CategoryBreakdown } from "./-components/CategoryBreakdown";
import { MetricCard, NetResultHeroCard } from "./-components/FinancialKpis";
import { MoneyFlowChart } from "./-components/MoneyFlowChart";
import { QuickLinksCard, ReceivablesCard } from "./-components/OverviewSidebar";
import { PeriodSelector } from "./-components/PeriodSelector";
import {
	getComparisonRange,
	getPeriodRange,
	type PeriodPreset,
} from "./-components/period";
import { RecentActivityCard } from "./-components/RecentActivityCard";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/financial/")({
	component: FinancialOverviewPage,
});

/**
 * Month-over-month change as a signed percent string ("+22.8%"), or null when
 * there is no prior result to compare against (a fresh account) — the caller
 * then shows a "new" chip instead of a fabricated delta.
 */
function formatDelta(current: number, previous: number): string | null {
	if (!Number.isFinite(previous) || previous === 0) {
		return null;
	}
	const pct = ((current - previous) / Math.abs(previous)) * 100;
	return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
}

function FinancialOverviewPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const scope = useFarmScope();
	const can = useHasPermission(orgId);
	const canManagePayroll = can(Action.MANAGE_PAYROLL);
	const canManageFinanceSettings = can(Action.MANAGE_FINANCE_SETTINGS);
	const [period, setPeriod] = useState<PeriodPreset>("thisMonth");

	const range = getPeriodRange(period);
	const previousRange = getComparisonRange(range, "previousPeriod", period);
	const establishmentId =
		scope.kind === "single" ? scope.establishmentId : undefined;

	const reportParams = {
		periodStart: range.periodStart,
		periodEnd: range.periodEnd,
		...(establishmentId ? { establishmentId } : {}),
	};

	// Updater form so the inherited `?farm=` scope survives the hop — the KPI
	// numbers are farm-scoped and must land on an identically-scoped report.
	const reportsTabSearch =
		(tab: "cashFlow" | "pnl") => (prev: Record<string, unknown>) => ({
			...prev,
			tab,
		});

	const { data: cashFlow, isLoading: cashFlowLoading } = useQuery(
		getCashFlowReportQueryOptions(orgId, reportParams),
	);
	const { data: pnl, isLoading: pnlLoading } = useQuery(
		getPnLReportQueryOptions(orgId, reportParams),
	);
	// Prior period's cash flow drives the Net-result delta chip only.
	const { data: previousCashFlow } = useQuery({
		...getCashFlowReportQueryOptions(orgId, {
			periodStart: previousRange?.periodStart ?? range.periodStart,
			periodEnd: previousRange?.periodEnd ?? range.periodEnd,
			...(establishmentId ? { establishmentId } : {}),
		}),
		enabled: previousRange != null,
	});
	const { data: recent } = useQuery(
		listFinancialTransactionsQueryOptions(orgId, {
			limit: "5",
			sortBy: "eventDate",
			sortOrder: "desc",
			...(establishmentId ? { establishmentId } : {}),
		}),
	);
	const { data: receivables } = useQuery(
		listReceivablesQueryOptions(orgId, {
			...(establishmentId ? { establishmentId } : {}),
		}),
	);

	const currency = cashFlow?.currency ?? "BRL";
	const netTotal = Number.parseFloat(cashFlow?.netTotal ?? "0");
	// Money-out renders with an explicit minus — but not on a zero, where a
	// signed "− R$ 0" reads as a glitch.
	const moneyOutSign =
		Number.parseFloat(cashFlow?.moneyOutTotal ?? "0") > 0 ? "− " : "";
	const netDelta = formatDelta(
		netTotal,
		Number.parseFloat(previousCashFlow?.netTotal ?? "0"),
	);

	const incomeCategoryCount = (pnl?.lines ?? []).filter(
		(line) => line.kind === "REVENUE",
	).length;
	const expenseCategoryCount = (pnl?.lines ?? []).filter(
		(line) => line.kind === "DIRECT_COST" || line.kind === "OVERHEAD",
	).length;

	const unreviewedCount = pnl?.unreviewedCount ?? 0;
	const recentRows = recent?.data ?? [];

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<p className="text-sm text-base-content/60">
					{t("financial.overview.subtitle")}
				</p>
				<PeriodSelector value={period} onChange={setPeriod} />
			</div>

			{cashFlowLoading || pnlLoading ? (
				<div className="flex justify-center py-16">
					<LoadingSpinner />
				</div>
			) : (
				<>
					{/* KPI row — Net result leads (hero); income/expenses support it.
					    Mobile: Net full-width, income/expenses side by side. */}
					<div className="grid grid-cols-2 gap-4 md:grid-cols-[1.35fr_1fr_1fr]">
						<div className="col-span-2 md:col-span-1">
							<NetResultHeroCard
								value={formatMoneyCompact(cashFlow?.netTotal ?? "0", currency)}
								exactValue={formatMoney(cashFlow?.netTotal ?? "0", currency)}
								delta={netDelta}
								to="/app/org/$orgId/financial/reports"
								params={{ orgId }}
								search={reportsTabSearch("pnl")}
							/>
						</div>
						<MetricCard
							tone="income"
							label={t("financial.overview.moneyIn")}
							value={formatMoneyCompact(
								cashFlow?.moneyInTotal ?? "0",
								currency,
							)}
							exactValue={formatMoney(cashFlow?.moneyInTotal ?? "0", currency)}
							sub={
								incomeCategoryCount > 0
									? t("financial.overview.kpi.incomeSub", {
											count: incomeCategoryCount,
										})
									: undefined
							}
							to="/app/org/$orgId/financial/reports"
							params={{ orgId }}
							search={reportsTabSearch("cashFlow")}
						/>
						<MetricCard
							tone="expense"
							label={t("financial.overview.moneyOut")}
							value={`${moneyOutSign}${formatMoneyCompact(cashFlow?.moneyOutTotal ?? "0", currency)}`}
							exactValue={`${moneyOutSign}${formatMoney(cashFlow?.moneyOutTotal ?? "0", currency)}`}
							sub={
								expenseCategoryCount > 0
									? t("financial.overview.kpi.expenseSub", {
											count: expenseCategoryCount,
										})
									: undefined
							}
							to="/app/org/$orgId/financial/reports"
							params={{ orgId }}
							search={reportsTabSearch("cashFlow")}
						/>
					</div>

					<AttentionLane
						orgId={orgId}
						unreviewedCount={unreviewedCount}
						canManageFinanceSettings={canManageFinanceSettings}
					/>

					{scope.kind === "single" &&
						pnl?.orgLevelExcluded &&
						pnl.orgLevelExcluded.count > 0 && (
							<p className="text-xs text-base-content/60">
								{t("financial.overview.orgLevelFootnote", {
									count: pnl.orgLevelExcluded.count,
								})}
							</p>
						)}

					{/* minmax(0,…) tracks + min-w-0 items: nowrap card content (truncated
					    rows, chart svg) must never widen the track past the viewport. */}
					<div className="grid gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
						<MoneyFlowChart
							buckets={cashFlow?.buckets ?? []}
							currency={currency}
						/>
						<CategoryBreakdown lines={pnl?.lines ?? []} currency={currency} />
					</div>

					<div className="grid gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
						<RecentActivityCard rows={recentRows} orgId={orgId} />
						<div className="flex min-w-0 flex-col gap-4">
							<ReceivablesCard
								orgId={orgId}
								summary={receivables?.summary}
								currency={currency}
							/>
							<QuickLinksCard
								orgId={orgId}
								canManagePayroll={canManagePayroll}
							/>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
